<script lang="ts" setup>
  import { ref, watch, onMounted, defineAsyncComponent, computed, nextTick } from 'vue'
  import {
    AgentActionsService,
    type AgentActionsListResponse,
    CasesService,
    CaseLifecycleWorkflowsService,
    type TCaseLifecycleWorkflow,
    type TCase,
    CaseLifecycleStatesService,
    type CustomerCaseLifecycleWorkflow as CaseLifecycleWorkflow,
    type TCaseLifecycleStateReasoning
  } from '@/types/generated'

  // Import specific action widgets
  import SendClientTemplateEmailWidget from './widgets/SendClientTemplateEmailWidget.vue'
  import AssignLawyerActionWidget from './widgets/AssignLawyerActionWidget.vue'
  import AssignLawDomainActionWidget from './widgets/AssignLawDomainActionWidget.vue'
  import AssignCaseLifecycleWorkflowActionWidget from './widgets/AssignCaseLifecycleWorkflowActionWidget.vue'
  import AssignCaseStatusActionWidget from './widgets/AssignCaseStatusActionWidget.vue'
  import SendAppointmentBookingLinkToClientActionWidget from './widgets/SendAppointmentBookingLinkToClientActionWidget.vue'
  import DraftWordDocumentTemplateActionWidget from './widgets/DraftWordDocumentTemplateActionWidget.vue'
  import AgentActionBaseWidget from './widgets/AgentActionBaseWidget.vue'

  import {
    Plus,
    Mail,
    UserPlus,
    FileText,
    GitBranch,
    ArrowRight,
    Calendar,
    Scale,
    Tag,
    AlertCircle,
    MoreVertical,
    RotateCw,
    Pause,
    Play,
    Clock,
    Info,
    X,
    ThumbsUp,
    ThumbsDown,
    Sparkles,
    ExternalLink
  } from 'lucide-vue-next'
  import { Button } from '@/components/ui/button'

  import { useCaseChannelSubscription } from '@/composables/useChannelSubscription'
  import { useTranslation } from '@/composables/useTranslation'
  import { useTimeFormatting } from '@/utils/timeFormatting'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import type { Option } from '@/components/admin/form/SelectField.vue'

  // Define props
  interface Props {
    caseUuid?: string
  }

  const props = withDefaults(defineProps<Props>(), {
    caseUuid: undefined
  })

  // Create a reactive case UUID from props
  const caseUuid = computed(() => props.caseUuid || '')

  // Check for debug mode via URL parameter
  const isDebugMode = computed(() => {
    if (typeof window === 'undefined') return false
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get('debug') === 'true'
  })

  // Case data for lifecycle state
  const caseData = ref<any | null>(null)

  // Workflow assignment data
  const workflows = ref<CaseLifecycleWorkflow[]>([])
  const currentWorkflowUuid = ref<string>('')

  // Translation setup
  const { t, locale } = useTranslation({
    en: {
      sendClientTemplateEmail: 'Send email to new mandate',
      caseAssignLawyer: 'Assign lawyer to case',
      draftWordDocumentTemplate: 'Draft Word document',
      caseAssignLifecycleWorkflow: 'Assign lifecycle workflow',
      caseChangeLifecycleState: 'Change lifecycle state',
      sendAppointmentBookingLink: 'Send appointment booking link',
      caseAssignLawDomain: 'Assign law domain',
      caseAssignStatus: 'Assign case status',
      unknownAction: 'Unknown action',
      aiActions: 'AI Actions',
      executedByUser: 'Executed by user',
      executedByAi: 'Executed by AI',
      cancelledByUser: 'Cancelled by user',
      cancelledByAi: 'Cancelled by AI',
      aiPoweredSuggestion: 'AI-powered suggestion',
      cancel: 'Cancel',
      confirm: 'Confirm',
      otherAiActions: 'Other AI actions',
      allAiActions: 'All AI actions',
      ai: 'AI',
      tabAll: 'All',
      tabWaitingForConfirmation: 'Waiting for confirmation',
      noActionsInCategory: "The AI hasn't executed or proposed any actions yet",
      showMoreActions: 'Show more',
      aiAgent: 'AI Agent',
      paused: 'Paused',
      running: 'Running',
      idle: 'Idle',
      advanced: 'Advanced',
      aiWorkflow: 'AI workflow',
      employmentLawStandard: 'Employment law standard',
      state: 'State',
      aborted: 'Aborted',
      stageOf: 'Stage {current} of {total}',
      latestAiReasoning: 'Latest AI Reasoning',
      viewAll: 'View all AI reasonings',
      aiReasoning: 'AI reasoning',
      processing: 'Processing...',
      pause: 'Pause',
      resume: 'Resume',
      allAiReasoning: 'All AI Reasoning',
      close: 'Close',
      noWorkflowAssigned: 'No workflow assigned',
      documentReview: 'Document Review',
      // Rating translations
      rateAction: 'Rate this action',
      negativeFeedback: 'What went wrong?',
      selectReason: 'Reason',
      selectReasonPlaceholder: 'Select a reason...',
      instructionsNotFollowed: 'Instructions not followed',
      payloadNotCorrect: 'Suggestion not correct',
      actionNotNeeded: 'Action not needed',
      other: 'Other',
      additionalComments: 'Additional comments (optional)',
      submitFeedback: 'Submit feedback',
      thankYouFeedback: 'Thank you for your feedback!',
      // Lifecycle state translations
      lifecycle_CREATED: 'Created',
      lifecycle_CLIENT_INFO_GATHERING: 'Client info gathering',
      lifecycle_PRE_CASE_CONTEXT_GATHERING: 'Pre-case context gathering',
      lifecycle_CASE_CONTEXT_GATHERING: 'Case context gathering',
      lifecycle_POST_CASE_CONTEXT_GATHERING: 'Additional context gathering',
      lifecycle_LEGAL_DOMAIN_CLASSIFICATION: 'Legal domain classification',
      lifecycle_WORKFLOW_ASSIGNMENT: 'Workflow assignment',
      lifecycle_READY_FOR_ONBOARDING_STEP_1: 'Ready for onboarding step 1',
      lifecycle_READY_FOR_ONBOARDING_STEP_2: 'Ready for onboarding step 2',
      lifecycle_DATA_COLLECTION: 'Data collection',
      lifecycle_ALL_DATA_COLLECTED: 'All data collected',
      lifecycle_DOCUMENT_DRAFTING: 'Document drafting',
      lifecycle_ALL_DOCUMENTS_DRAFTED: 'All documents drafted',
      lifecycle_READY_FOR_EXPORT: 'Ready for export',
      lifecycle_EXPORTED: 'Exported',
      lifecycle_ABORTED: 'Aborted'
    },
    de: {
      sendClientTemplateEmail: 'E-Mail an Mandanten senden',
      caseAssignLawyer: 'Sachbearbeiter zuweisen',
      draftWordDocumentTemplate: 'Word-Dokument erstellen',
      caseAssignLifecycleWorkflow: 'KI-Workflow zuweisen',
      caseChangeLifecycleState: 'Lebenszyklus-Status ändern',
      sendAppointmentBookingLink: 'Terminbuchungslink senden',
      caseAssignLawDomain: 'Rechtsgebiet zuweisen',
      caseAssignStatus: 'Status der Anfrage zuweisen',
      unknownAction: 'Unbekannte Aktion',
      aiActions: 'KI-Aktionen',
      executedByUser: 'Ausgeführt von Benutzer',
      executedByAi: 'Ausgeführt von KI',
      cancelledByUser: 'Abgebrochen von Benutzer',
      cancelledByAi: 'Abgebrochen von KI',
      aiPoweredSuggestion: 'KI-Vorschlag',
      cancel: 'Abbrechen',
      confirm: 'Bestätigen',
      otherAiActions: 'Weitere KI-Aktionen',
      allAiActions: 'Alle KI-Aktionen',
      ai: 'KI',
      tabAll: 'Alle',
      tabWaitingForConfirmation: 'Warte auf Bestätigung',
      noActionsInCategory: 'Die KI hat noch keine Aktionen ausgeführt bzw. vorgeschlagen',
      showMoreActions: 'weitere anzeigen',
      aiAgent: 'KI-Agent',
      paused: 'Pausiert',
      running: 'Läuft',
      idle: 'Bereit',
      advanced: 'Erweitert',
      aiWorkflow: 'KI-Workflow',
      employmentLawStandard: 'Arbeitsrecht Standard',
      state: 'Status',
      aborted: 'Abgebrochen',
      stageOf: 'Stufe {current} von {total}',
      latestAiReasoning: 'Neueste KI-Überlegung',
      viewAll: 'Alle KI-Überlegungen anzeigen',
      aiReasoning: 'KI-Überlegung',
      processing: 'Verarbeitung...',
      pause: 'Pausieren',
      resume: 'Fortsetzen',
      allAiReasoning: 'Alle KI-Überlegungen',
      close: 'Schließen',
      noWorkflowAssigned: 'Kein Workflow zugewiesen',
      documentReview: 'Dokumentenprüfung',
      // Rating translations
      rateAction: 'Diese Aktion bewerten',
      negativeFeedback: 'Was ist schiefgelaufen?',
      selectReason: 'Grund',
      selectReasonPlaceholder: 'Grund auswählen...',
      instructionsNotFollowed: 'Anweisungen nicht befolgt',
      payloadNotCorrect: 'Vorschlag nicht korrekt',
      actionNotNeeded: 'Aktion nicht benötigt',
      other: 'Sonstiges',
      additionalComments: 'Zusätzliche Kommentare (optional)',
      submitFeedback: 'Feedback senden',
      thankYouFeedback: 'Danke für dein Feedback!',
      // Lifecycle state translations
      lifecycle_CREATED: 'Angelegt',
      lifecycle_CLIENT_INFO_GATHERING: 'Mandantendaten erfassen',
      lifecycle_PRE_CASE_CONTEXT_GATHERING: 'Vorab-Kontext erfassen',
      lifecycle_CASE_CONTEXT_GATHERING: 'Anfragenkontext erfassen',
      lifecycle_POST_CASE_CONTEXT_GATHERING: 'Weiteren Kontext erfassen',
      lifecycle_LEGAL_DOMAIN_CLASSIFICATION: 'Rechtsgebiet bestimmen',
      lifecycle_WORKFLOW_ASSIGNMENT: 'KI-Workflow zuweisen',
      lifecycle_READY_FOR_ONBOARDING_STEP_1: 'Onboarding Schritt 1',
      lifecycle_READY_FOR_ONBOARDING_STEP_2: 'Onboarding Schritt 2',
      lifecycle_DATA_COLLECTION: 'Datenerhebung',
      lifecycle_ALL_DATA_COLLECTED: 'Alle Daten erfasst',
      lifecycle_DOCUMENT_DRAFTING: 'Dokumente erstellen',
      lifecycle_ALL_DOCUMENTS_DRAFTED: 'Alle Dokumente erstellt',
      // deliberately rename those, because we don't support export yet
      lifecycle_READY_FOR_EXPORT: 'Akte bereit',
      lifecycle_EXPORTED: 'Akte aufgenommen',
      lifecycle_ABORTED: 'Abgebrochen'
    }
  })
  const { formatTimeAgo } = useTimeFormatting(locale)

  // Action type enum (matching backend)
  enum ActionTypeEnum {
    SEND_CLIENT_TEMPLATE_EMAIL = 'SEND_CLIENT_TEMPLATE_EMAIL',
    CASE_ASSIGN_LAWYER = 'CASE_ASSIGN_LAWYER',
    DRAFT_WORD_DOCUMENT_TEMPLATE = 'DRAFT_WORD_DOCUMENT_TEMPLATE',
    CASE_ASSIGN_LIFECYCLE_WORKFLOW = 'CASE_ASSIGN_LIFECYCLE_WORKFLOW',
    SEND_APPOINTMENT_BOOKING_LINK_TO_CLIENT = 'SEND_APPOINTMENT_BOOKING_LINK_TO_CLIENT',
    CASE_ASSIGN_LAW_DOMAIN = 'CASE_ASSIGN_LAW_DOMAIN',
    CASE_ASSIGN_STATUS = 'CASE_ASSIGN_STATUS'
  }

  // Comprehensive action map with icons and titles
  const actionMap = {
    [ActionTypeEnum.SEND_CLIENT_TEMPLATE_EMAIL]: {
      icon: Mail,
      title: () => t('sendClientTemplateEmail')
    },
    [ActionTypeEnum.CASE_ASSIGN_LAWYER]: {
      icon: UserPlus,
      title: () => t('caseAssignLawyer')
    },
    [ActionTypeEnum.DRAFT_WORD_DOCUMENT_TEMPLATE]: {
      icon: FileText,
      title: () => t('draftWordDocumentTemplate')
    },
    [ActionTypeEnum.CASE_ASSIGN_LIFECYCLE_WORKFLOW]: {
      icon: GitBranch,
      title: () => t('caseAssignLifecycleWorkflow')
    },
    [ActionTypeEnum.SEND_APPOINTMENT_BOOKING_LINK_TO_CLIENT]: {
      icon: Calendar,
      title: () => t('sendAppointmentBookingLink')
    },
    [ActionTypeEnum.CASE_ASSIGN_LAW_DOMAIN]: {
      icon: Scale,
      title: () => t('caseAssignLawDomain')
    },
    [ActionTypeEnum.CASE_ASSIGN_STATUS]: {
      icon: Tag,
      title: () => t('caseAssignStatus')
    }
  }

  // Widget mapping - maps action types to their corresponding widgets
  const actionWidgetMap = {
    [ActionTypeEnum.SEND_CLIENT_TEMPLATE_EMAIL]: SendClientTemplateEmailWidget,
    [ActionTypeEnum.CASE_ASSIGN_LAWYER]: AssignLawyerActionWidget,
    [ActionTypeEnum.CASE_ASSIGN_LAW_DOMAIN]: AssignLawDomainActionWidget,
    [ActionTypeEnum.CASE_ASSIGN_LIFECYCLE_WORKFLOW]: AssignCaseLifecycleWorkflowActionWidget,
    [ActionTypeEnum.CASE_ASSIGN_STATUS]: AssignCaseStatusActionWidget,
    [ActionTypeEnum.SEND_APPOINTMENT_BOOKING_LINK_TO_CLIENT]:
      SendAppointmentBookingLinkToClientActionWidget,
    [ActionTypeEnum.DRAFT_WORD_DOCUMENT_TEMPLATE]: DraftWordDocumentTemplateActionWidget
    // Add more mappings as needed
  }

  // Helper methods
  const getActionInfo = (action: any) => {
    const actionInfo = actionMap[action.action_type as ActionTypeEnum]
    if (actionInfo) {
      return {
        icon: actionInfo.icon,
        title: actionInfo.title()
      }
    }

    // Fallback for unknown actions
    return {
      icon: AlertCircle,
      title: action.action_type?.replace(/_/g, ' ') || t('unknownAction')
    }
  }

  const getActionTitle = (action: any) => {
    return getActionInfo(action).title
  }

  // Function to get the appropriate widget component for an action type
  const getWidgetComponent = (actionType: string) => {
    const component = (actionWidgetMap as any)[actionType]
    return component || AgentActionBaseWidget
  }

  // Reactive data
  const agentActions = ref<AgentActionsListResponse | null>(null)
  const error = ref<string | null>(null)
  const actionPayloads = ref<Record<string, any>>({})

  // Loading states for individual actions
  const committingActions = ref<Set<string>>(new Set())
  const revertingActions = ref<Set<string>>(new Set())

  // Track which action's reasoning popover is open
  const openReasoningPopover = ref<string | null>(null)

  const toggleReasoningPopover = (actionUuid: string) => {
    if (openReasoningPopover.value === actionUuid) {
      openReasoningPopover.value = null
    } else {
      openReasoningPopover.value = actionUuid
    }
  }

  // Rating state
  const ratingModalActionUuid = ref<string | null>(null)
  const ratingNegativeVariant = ref<string>('')
  const ratingNegativeComment = ref<string>('')
  const isSubmittingRating = ref(false)
  // Track rated actions: 'positive' or 'negative'
  const actionRatings = ref<Record<string, 'positive' | 'negative'>>({})

  // Rating functions
  const handlePositiveRating = async (actionUuid: string) => {
    if (actionRatings.value[actionUuid]) return

    isSubmittingRating.value = true
    try {
      await AgentActionsService.agentActionsRateCreate({
        requestBody: {
          agent_action_uuid: actionUuid,
          rating: '5.0'
        }
      })
      actionRatings.value[actionUuid] = 'positive'
    } catch (error) {
      console.error('Error submitting positive rating:', error)
    } finally {
      isSubmittingRating.value = false
    }
  }

  const openNegativeRatingModal = (actionUuid: string) => {
    if (actionRatings.value[actionUuid]) return
    ratingModalActionUuid.value = actionUuid
    ratingNegativeVariant.value = ''
    ratingNegativeComment.value = ''
  }

  const closeNegativeRatingModal = () => {
    ratingModalActionUuid.value = null
    ratingNegativeVariant.value = ''
    ratingNegativeComment.value = ''
  }

  const submitNegativeRating = async () => {
    if (!ratingModalActionUuid.value || !ratingNegativeVariant.value) return

    isSubmittingRating.value = true
    try {
      await AgentActionsService.agentActionsRateCreate({
        requestBody: {
          agent_action_uuid: ratingModalActionUuid.value,
          rating: '0.0',
          rating_negative_variant: ratingNegativeVariant.value as any,
          rating_negative_comment: ratingNegativeComment.value || undefined
        }
      })
      actionRatings.value[ratingModalActionUuid.value] = 'negative'
      closeNegativeRatingModal()
    } catch (error) {
      console.error('Error submitting negative rating:', error)
    } finally {
      isSubmittingRating.value = false
    }
  }

  // Workflow rating state
  const workflowRating = ref<'positive' | 'negative' | null>(null)
  const workflowRatingModalOpen = ref(false)
  const workflowRatingComment = ref('')
  const isSubmittingWorkflowRating = ref(false)

  // Workflow rating functions
  const handlePositiveWorkflowRating = async () => {
    if (workflowRating.value || !caseData.value?.case_lifecycle_state?.uuid) return

    isSubmittingWorkflowRating.value = true
    try {
      await CaseLifecycleStatesService.caseLifecycleStatesRateCreate({
        uuid: caseData.value.case_lifecycle_state.uuid,
        requestBody: {
          rating: '5.0'
        }
      })
      workflowRating.value = 'positive'
    } catch (error) {
      console.error('Error submitting positive workflow rating:', error)
    } finally {
      isSubmittingWorkflowRating.value = false
    }
  }

  const openNegativeWorkflowRatingModal = () => {
    if (workflowRating.value) return
    workflowRatingModalOpen.value = true
    workflowRatingComment.value = ''
  }

  const closeNegativeWorkflowRatingModal = () => {
    workflowRatingModalOpen.value = false
    workflowRatingComment.value = ''
  }

  const submitNegativeWorkflowRating = async () => {
    if (!caseData.value?.case_lifecycle_state?.uuid) return

    isSubmittingWorkflowRating.value = true
    try {
      await CaseLifecycleStatesService.caseLifecycleStatesRateCreate({
        uuid: caseData.value.case_lifecycle_state.uuid,
        requestBody: {
          rating: '0.0',
          rating_negative_comment: workflowRatingComment.value || undefined
        }
      })
      workflowRating.value = 'negative'
      closeNegativeWorkflowRatingModal()
    } catch (error) {
      console.error('Error submitting negative workflow rating:', error)
    } finally {
      isSubmittingWorkflowRating.value = false
    }
  }

  // Case data for lifecycle state
  const isLoadingCase = ref(false)
  const caseError = ref<string | null>(null)

  // Workflow assignment data
  const isLoadingWorkflows = ref(false)
  const workflowError = ref<string | null>(null)

  // Agent activity banner state
  const currentAgentActivityType = ref<string | undefined>(undefined)

  // Widget display state
  const displayedActionCount = ref(1) // Show only 1 action initially

  // Tab state - Tab types for filtering actions
  enum TabType {
    ALL = 'all',
    WAITING_FOR_CONFIRMATION = 'waiting_for_confirmation'
  }

  // Selected tab state - preselect "waiting for confirmation"
  const selectedTab = ref<TabType>(TabType.WAITING_FOR_CONFIRMATION)

  // Helper to get waiting actions count
  const waitingActionsCount = computed(() => {
    if (!agentActions.value?.results || !Array.isArray(agentActions.value.results)) return 0
    return agentActions.value.results.filter(
      (action) => !action.is_committed && !action.is_reverted
    ).length
  })

  // Helper to get total actions count
  const totalActionsCount = computed(() => {
    if (!agentActions.value?.results || !Array.isArray(agentActions.value.results)) return 0
    return agentActions.value.results.length
  })

  // Tab configuration
  const tabs = computed(() => [
    {
      key: TabType.ALL,
      label: () => t('tabAll'),
      showCount: true,
      count: totalActionsCount.value
    },
    {
      key: TabType.WAITING_FOR_CONFIRMATION,
      label: () => t('tabWaitingForConfirmation'),
      showCount: true,
      count: waitingActionsCount.value
    }
  ])

  // Debounce refresh requests to prevent excessive API calls
  let refreshTimeout: ReturnType<typeof setTimeout> | null = null
  const debouncedRefresh = (delay = 500) => {
    if (refreshTimeout) {
      clearTimeout(refreshTimeout)
    }
    refreshTimeout = setTimeout(() => {
      fetchAgentActions()
    }, delay)
  }

  // Set up channel subscription with automatic lifecycle management for case_agent_activity only
  const {
    connectionStatus,
    lastMessage,
    lastError,
    isConnected,
    isConnecting,
    isReconnecting,
    hasError,
    reconnectAttempts
  } = useCaseChannelSubscription(caseUuid, {
    autoReconnect: true,
    maxReconnectAttempts: 5,
    reconnectDelay: 1000
  })

  // Handle incoming WebSocket messages - only listen for case_agent_activity
  watch(lastMessage, async (message) => {
    if (!message) return

    console.log('Received case update in AgentActionWidget:', message)

    // Only handle case_agent_activity messages
    if (message.type === 'case_agent_activity') {
      // Handle case agent activity notifications
      if (message.data?.case_uuid === caseUuid.value) {
        console.log('🤖 Case Agent Activity:', {
          activityType: message.data.activity_type,
          status: message.data.status,
          timestamp: message.data.timestamp,
          caseUuid: message.data.case_uuid
        })

        // Handle agent activity banner
        if (message.data.status === 'STARTED') {
          isAgentWorking.value = true
          currentAgentActivityType.value = message.data.activity_type
        } else if (message.data.status === 'FINISHED') {
          isAgentWorking.value = false
          currentAgentActivityType.value = undefined

          // Reload actions and case data when agent finishes
          await Promise.all([fetchAgentActions(), fetchCaseData()])
        }
      }
    }
  })

  // Fetch case data to get lifecycle state
  const fetchCaseData = async () => {
    if (!props.caseUuid) {
      caseData.value = null
      return
    }

    isLoadingCase.value = true
    caseError.value = null

    try {
      const response = await CasesService.casesRetrieve({
        uuid: props.caseUuid
      })
      caseData.value = response

      // Initialize workflow rating from case lifecycle state
      if (
        response.case_lifecycle_state?.rating !== null &&
        response.case_lifecycle_state?.rating !== undefined
      ) {
        const ratingValue = parseFloat(String(response.case_lifecycle_state.rating))
        workflowRating.value = ratingValue >= 2.5 ? 'positive' : 'negative'
      } else {
        workflowRating.value = null
      }
    } catch (err) {
      caseError.value = 'Failed to fetch case data'
      console.error('Error fetching case data:', err)
    } finally {
      isLoadingCase.value = false
    }
  }

  // Fetch agent actions
  const fetchAgentActions = async () => {
    if (!props.caseUuid) {
      agentActions.value = null
      return
    }

    error.value = null

    try {
      const response = await AgentActionsService.agentActionsList({
        caseUuid: props.caseUuid,
        ordering: '-created_at'
      })

      // Smoothly update the data - Vue's reactivity will handle efficient updates
      agentActions.value = response

      // Initialize ratings from fetched data
      if (response.results) {
        for (const action of response.results) {
          if (action.rating !== null && action.rating !== undefined) {
            // rating of 5.0 = positive, rating of 0.0 = negative
            const ratingValue = parseFloat(String(action.rating))
            actionRatings.value[action.uuid] = ratingValue >= 2.5 ? 'positive' : 'negative'
          }
        }
      }
    } catch (err) {
      error.value = 'Failed to fetch agent actions'
      console.error('Error fetching agent actions:', err)
    }
  }

  // Handle action committed/reverted events
  const handleActionCommitted = async (uuid: string) => {
    console.log('Action committed:', uuid)

    // Set loading state
    committingActions.value.add(uuid)

    try {
      const requestBody: any = {
        agent_action_uuid: uuid
      }

      // Include custom payload if provided
      const customPayload = getCustomPayload(uuid)
      if (customPayload) {
        requestBody.payload = customPayload
      }

      await AgentActionsService.agentActionsCommitCreate({
        requestBody
      })

      console.log('Action committed successfully:', uuid)
    } catch (error) {
      console.error('Error committing action:', error)
    } finally {
      // Clear loading state
      committingActions.value.delete(uuid)
    }

    // Use debounced refresh to prevent excessive API calls
    debouncedRefresh(300)
  }

  const handleActionReverted = async (uuid: string) => {
    console.log('Action reverted:', uuid)

    // Set loading state
    revertingActions.value.add(uuid)

    try {
      // Find the action to get its revert payload
      const action = agentActions.value?.results.find((a) => a.uuid === uuid)

      await AgentActionsService.agentActionsRevertCreate({
        requestBody: {
          agent_action_uuid: uuid,
          revert_payload: action?.revert_payload || null
        }
      })

      console.log('Action reverted successfully:', uuid)
    } catch (error) {
      console.error('Error reverting action:', error)
    } finally {
      // Clear loading state
      revertingActions.value.delete(uuid)
    }

    // Clear any custom payload for this action
    delete actionPayloads.value[uuid]
    // Use debounced refresh to prevent excessive API calls
    debouncedRefresh(300)
  }

  // Handle payload changes from child widgets
  const handlePayloadChanged = (actionUuid: string, payload: any) => {
    actionPayloads.value[actionUuid] = payload
  }

  // Get the custom payload for an action, if any
  const getCustomPayload = (actionUuid: string) => {
    return actionPayloads.value[actionUuid]
  }

  // Fetch available workflows
  const fetchWorkflows = async () => {
    isLoadingWorkflows.value = true
    workflowError.value = null

    try {
      const response = await CaseLifecycleWorkflowsService.caseLifecycleWorkflowsList({
        limit: 100
      })
      workflows.value = response.results || []
    } catch (error) {
      console.error('Error fetching workflows:', error)
      workflowError.value = 'Failed to load workflows'
      workflows.value = []
    } finally {
      isLoadingWorkflows.value = false
    }
  }

  // Fetch current workflow assignment
  const fetchCurrentWorkflow = async () => {
    if (!props.caseUuid) return

    try {
      // For now, we'll start with no workflow assigned
      // In a real implementation, you might want to add a separate endpoint
      // to get the current workflow assignment, or include it in the case data
      currentWorkflowUuid.value = ''
    } catch (error) {
      console.error('Error fetching current workflow:', error)
    }
  }

  // Computed properties for the widget
  const filteredActions = computed(() => {
    if (!agentActions.value?.results || !Array.isArray(agentActions.value.results)) return []
    return agentActions.value.results
  })

  const displayedActions = computed(() => {
    return filteredActions.value
  })

  const showAllActions = () => {
    // Navigate to all actions page or expand all actions
    displayedActionCount.value = filteredActions.value.length || 1
  }

  // Current tab title for header - always show generic "AI Actions"
  const currentTabTitle = computed(() => {
    return t('aiActions')
  })

  // MERGE
  // Time formatting setup

  // Reasoning data
  const allReasonings = ref<TCaseLifecycleStateReasoning[]>([])
  const showAllReasoningsModal = ref(false)

  // Agent activity state - tracks real agent activity from WebSocket
  const activeActivities = ref<Set<string>>(new Set())
  const isAgentWorking = computed(() => activeActivities.value.size > 0)

  // Computed properties for the new design
  const lifecycleStateDisplay = computed(() => {
    if (!caseData.value?.case_lifecycle_state?.current_state) {
      return t('documentReview') // Default state shown in the screenshot
    }

    const state = caseData.value.case_lifecycle_state.current_state as string
    const key = `lifecycle_${state}`
    const label = (t as unknown as (k: string) => string)(key)

    // If translation exists, use it; otherwise fallback to humanized
    if (label && label !== key) return label

    return state
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  })

  // Lifecycle state mapping for progress calculation
  // Maps each state from CaseLifecycleState.STATES to its sequential stage number
  const LIFECYCLE_STATES = {
    CREATED: 1, // Initial state
    CLIENT_INFO_GATHERING: 2, // Gathering client information
    PRE_CASE_CONTEXT_GATHERING: 3, // Pre-case context gathering
    CASE_CONTEXT_GATHERING: 4, // Main case context gathering
    POST_CASE_CONTEXT_GATHERING: 5, // Post-case context gathering
    LEGAL_DOMAIN_CLASSIFICATION: 6, // Legal domain classification
    WORKFLOW_ASSIGNMENT: 7, // Workflow assignment
    READY_FOR_ONBOARDING_STEP_1: 8, // First onboarding step
    READY_FOR_ONBOARDING_STEP_2: 9, // Second onboarding step
    DATA_COLLECTION: 10, // Data collection phase
    ALL_DATA_COLLECTED: 11, // Data collection complete
    DOCUMENT_DRAFTING: 12, // Document drafting phase
    ALL_DOCUMENTS_DRAFTED: 13, // Document drafting complete
    READY_FOR_EXPORT: 14, // Ready for export
    EXPORTED: 15, // Final state - exported
    ABORTED: 15 // Special case - treated as final stage but with error state
  } as const

  // Calculate actual progress based on lifecycle state
  const currentStage = computed(() => {
    if (!caseData.value?.case_lifecycle_state?.current_state) {
      return 4 // Default to stage 4 to match the screenshot (CASE_CONTEXT_GATHERING)
    }

    const state = caseData.value.case_lifecycle_state.current_state
    return LIFECYCLE_STATES[state as keyof typeof LIFECYCLE_STATES] || 1
  })

  const totalStages = computed(() => 15) // Total number of stages (excluding ABORTED as a separate flow)

  const progressPercentage = computed(() => {
    const percentage = (currentStage.value / totalStages.value) * 100
    return Math.min(percentage, 100) // Cap at 100%
  })

  // Check if the case is aborted
  const isAborted = computed(() => {
    return caseData.value?.case_lifecycle_state?.current_state === 'ABORTED'
  })

  // Check if the agent is active
  const isAgentActive = computed(() => {
    return caseData.value?.case_lifecycle_state?.is_agent_active ?? false
  })

  // Format stage display with proper variable substitution
  const stageDisplay = computed(() => {
    if (isAborted.value) {
      return t('aborted')
    }

    const current = currentStage.value
    const total = totalStages.value

    // Use locale-specific formatting
    if (locale.value === 'de') {
      return `Stufe ${current} von ${total}`
    } else {
      return `Stage ${current} of ${total}`
    }
  })

  // Format view all text
  const viewAllText = computed(() => {
    return t('viewAll')
  })

  // Workflow options for SelectField
  const workflowOptions = computed<Option[]>(() => {
    const options: Option[] = workflows.value.map((workflow) => ({
      value: workflow.uuid || '',
      title: workflow.name || 'Unnamed Workflow'
    }))

    // Add "No workflow" option
    options.unshift({
      value: '',
      title: t('noWorkflowAssigned')
    })

    return options
  })

  // Get the single latest reasoning entry for display
  const latestReasoning = computed(() => {
    // Mock reasoning data for now - replace with actual reasoning logic
    const mockReasoning = {
      uuid: '1',
      status: 'completed',
      reasoning: { en: "Email content analyzed: 'Formation of a GmbH' indicates company law." },
      created_at: new Date().toISOString()
    }

    // If we have real reasoning data, use the latest one, otherwise show mock data
    if (allReasonings.value.length > 0) {
      const latest = allReasonings.value[0] // Assuming they're ordered by latest first
      return {
        ...latest,
        status: 'completed' // For now, assume completed
      }
    }

    return mockReasoning
  })

  // Get all reasoning entries with mock data fallback for modal
  const allReasoningsForModal = computed(() => {
    // Mock reasoning data for modal
    const mockReasonings = [
      {
        uuid: '1',
        status: 'completed',
        reasoning: { en: "Email content analyzed: 'Formation of a GmbH' indicates company law." },
        created_at: new Date(Date.now() - 5 * 60 * 1000).toISOString() // 5 minutes ago
      },
      {
        uuid: '2',
        status: 'completed',
        reasoning: {
          en: 'Document classification completed. Legal domain identified as corporate law based on keywords and context.'
        },
        created_at: new Date(Date.now() - 15 * 60 * 1000).toISOString() // 15 minutes ago
      },
      {
        uuid: '3',
        status: 'completed',
        reasoning: {
          en: 'Initial case assessment: Client inquiry about GmbH formation process. Requires corporate law expertise.'
        },
        created_at: new Date(Date.now() - 30 * 60 * 1000).toISOString() // 30 minutes ago
      }
    ]

    // If we have real reasoning data, use it, otherwise show mock data
    if (allReasonings.value.length > 0) {
      return allReasonings.value.map((reasoning) => ({
        ...reasoning,
        status: 'completed' // For now, assume completed
      }))
    }

    return mockReasonings
  })

  // Handle incoming WebSocket messages - simplified version
  watch(lastMessage, async (message) => {
    if (!message) return

    console.log('Received case update in AgentState:', message)

    // Handle case_agent_activity messages
    if (message.type === 'case_agent_activity' && message.data?.case_uuid === caseUuid.value) {
      if (message.data.status === 'STARTED') {
        activeActivities.value.add(message.data.activity_type)
      } else if (message.data.status === 'FINISHED') {
        activeActivities.value.delete(message.data.activity_type)
        // Reload data when agent finishes
        await Promise.all([fetchCaseData(), fetchReasoningData()])
      }
    }

    // Handle case_document_processing messages
    if (message.type === 'case_document_processing' && message.data?.case_uuid === caseUuid.value) {
      if (message.data.status === 'STARTED') {
        activeActivities.value.add('document_processing')
      } else if (message.data.status === 'FINISHED') {
        activeActivities.value.delete('document_processing')
        // Reload data when document processing finishes
        await Promise.all([fetchCaseData(), fetchReasoningData()])
      }
    }
  })

  // Handle workflow assignment change
  const handleWorkflowChange = async (workflowUuid: string) => {
    if (!props.caseUuid || !caseData.value?.case_lifecycle_state?.uuid) return

    try {
      await CaseLifecycleStatesService.caseLifecycleStatesPartialUpdate({
        uuid: caseData.value.case_lifecycle_state.uuid,
        requestBody: {
          workflow_uuid: workflowUuid || null
        }
      })

      console.log('Workflow assignment updated successfully')
      await fetchCaseData()
    } catch (error) {
      console.error('Error updating workflow assignment:', error)
    }
  }

  // Fetch reasoning data for the current case
  const fetchReasoningData = async () => {
    if (!props.caseUuid) {
      allReasonings.value = []
      return
    }

    try {
      const response = await fetch(
        `/api/v3/case_lifecycle_state_reasoning/?limit=100&case_uuid=${props.caseUuid}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          }
        }
      )

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      allReasonings.value = data.results || []
    } catch (err) {
      console.error('Error fetching reasoning data:', err)
      allReasonings.value = []
    }
  }

  // Toggle agent active state (pause/resume)
  const toggleAgent = async () => {
    if (!props.caseUuid || !caseData.value?.case_lifecycle_state?.uuid) return

    const newActiveState = !isAgentActive.value

    try {
      await CaseLifecycleStatesService.caseLifecycleStatesPartialUpdate({
        uuid: caseData.value.case_lifecycle_state.uuid,
        requestBody: {
          is_agent_active: newActiveState
        }
      })

      console.log(`Agent ${newActiveState ? 'resumed' : 'paused'} successfully`)
      await fetchCaseData()
    } catch (error) {
      console.error(`Error ${newActiveState ? 'resuming' : 'pausing'} agent:`, error)
    }
  }
  // END MERGE

  // Watch for case_uuid changes
  watch(
    () => props.caseUuid,
    (newUuid, oldUuid) => {
      // Only fetch if the UUID actually changed
      if (newUuid !== oldUuid) {
        // Reset state when switching cases
        agentActions.value = null
        error.value = null

        if (newUuid) {
          fetchCaseData()
          fetchAgentActions()
          fetchCurrentWorkflow()
          fetchReasoningData()
        }
      }
    },
    { immediate: true }
  )

  // Fetch on mount
  onMounted(async () => {
    await Promise.all([
      fetchCaseData(),
      fetchAgentActions(),
      fetchWorkflows(),
      fetchCurrentWorkflow(),
      fetchReasoningData()
    ])
  })
</script>

<template>
  <div
    v-if="props.caseUuid && agentActions && allReasonings"
    class="agent-actions-widget card mb-3 rounded-lg border border-gray-200 shadow-sm"
  >
    <div>
      <!-- Header -->
      <div class="card-header">
        <div class="flex w-full items-center justify-between">
          <h3 class="card-title mb-0">{{ t('aiWorkflow') }}</h3>
          <div class="flex items-center gap-2">
            <!-- Status Badge -->
            <div
              class="rounded-full px-2 py-1 text-xs font-medium"
              :class="{
                'bg-green-100 text-green-700': isAgentActive && isAgentWorking,
                'bg-purple-100 text-purple-700': isAgentActive && !isAgentWorking,
                'bg-gray-100 text-gray-700': !isAgentActive
              }"
            >
              {{ !isAgentActive ? t('paused') : isAgentWorking ? t('running') : t('idle') }}
            </div>
            <!-- Pause/Resume Button -->
            <Button
              :variant="isAgentActive ? 'secondary' : 'default'"
              size="icon"
              :class="{
                'h-8 w-8': true,
                'bg-purple-600 text-white hover:bg-purple-700': !isAgentActive
              }"
              @click="toggleAgent"
            >
              <Pause
                v-if="isAgentActive"
                :size="14"
              />
              <Play
                v-else
                :size="14"
              />
            </Button>
          </div>
        </div>
      </div>
    </div>

    <!-- All Reasonings Modal -->
    <div
      v-if="showAllReasoningsModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      @click.self="showAllReasoningsModal = false"
    >
      <div class="mx-4 max-h-[80vh] w-full max-w-2xl overflow-hidden rounded-lg bg-white shadow-xl">
        <!-- Modal Header -->
        <div class="flex items-center justify-between border-b border-gray-200 p-6">
          <h3 class="text-lg font-semibold text-gray-900">{{ t('allAiReasoning') }}</h3>
          <button
            class="text-gray-400 transition-colors hover:text-gray-600"
            @click="showAllReasoningsModal = false"
          >
            <svg
              class="h-6 w-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              ></path>
            </svg>
          </button>
        </div>

        <!-- Modal Content -->
        <div class="max-h-[calc(80vh-120px)] overflow-y-auto p-6">
          <div class="space-y-4">
            <div
              v-for="reasoning in allReasoningsForModal"
              :key="reasoning.uuid"
              class="space-y-2"
            >
              <!-- Header outside the widget -->
              <div class="flex items-start justify-between">
                <div class="flex items-center gap-2 text-xs text-gray-500">
                  <Clock :size="14" />
                  {{ formatTimeAgo(reasoning.created_at || new Date().toISOString()) }}
                  <span class="ml-2 text-gray-400">{{ t('aiReasoning') }}</span>
                </div>
              </div>

              <!-- Purple bordered reasoning widget -->
              <div class="relative rounded border border-purple-300 bg-purple-50 px-3 py-2">
                <div class="absolute right-2 top-2">
                  <div class="rounded bg-purple-100 px-1.5 py-0.5 text-xs text-purple-600">
                    {{ t('ai') }}
                  </div>
                </div>
                <div class="flex gap-2">
                  <div class="mt-1 flex-shrink-0">
                    <div
                      v-if="reasoning.status !== 'completed'"
                      class="flex h-5 w-5 items-center justify-center rounded-full bg-purple-100"
                    >
                      <RotateCw class="h-3 w-3 animate-spin text-purple-600" />
                    </div>
                  </div>
                  <div class="flex-1 pr-8">
                    <p class="text-sm leading-relaxed text-gray-600">
                      {{ reasoning.reasoning?.[locale] || t('processing') }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="flex justify-end border-t border-gray-200 p-6">
          <Button
            variant="secondary"
            @click="showAllReasoningsModal = false"
          >
            {{ t('close') }}
          </Button>
        </div>
      </div>
    </div>

    <!-- Negative Rating Modal -->
    <div
      v-if="ratingModalActionUuid"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      @click.self="closeNegativeRatingModal"
    >
      <div class="mx-4 w-full max-w-md overflow-hidden rounded-lg bg-white shadow-xl">
        <!-- Modal Header -->
        <div class="flex items-center justify-between border-b border-gray-200 p-4">
          <h3 class="text-lg font-semibold text-gray-900">{{ t('negativeFeedback') }}</h3>
          <button
            class="text-gray-400 transition-colors hover:text-gray-600"
            @click="closeNegativeRatingModal"
          >
            <X :size="20" />
          </button>
        </div>

        <!-- Modal Content -->
        <div class="space-y-4 p-4">
          <!-- Variant Selection -->
          <div>
            <select
              v-model="ratingNegativeVariant"
              class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500
                focus:outline-none focus:ring-1 focus:ring-purple-500"
            >
              <option
                value=""
                disabled
              >
                {{ t('selectReasonPlaceholder') }}
              </option>
              <option value="INSTRUCTIONS_NOT_FOLLOWED">{{ t('instructionsNotFollowed') }}</option>
              <option value="PAYLOAD_NOT_CORRECT">{{ t('payloadNotCorrect') }}</option>
              <option value="ACTION_NOT_NEEDED">{{ t('actionNotNeeded') }}</option>
              <option value="OTHER">{{ t('other') }}</option>
            </select>
          </div>

          <!-- Comment -->
          <div>
            <label class="mb-1 block text-sm font-medium text-gray-700">
              {{ t('additionalComments') }}
            </label>
            <textarea
              v-model="ratingNegativeComment"
              rows="3"
              class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500
                focus:outline-none focus:ring-1 focus:ring-purple-500"
            ></textarea>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="flex justify-end gap-2 border-t border-gray-200 p-4">
          <Button
            variant="secondary"
            @click="closeNegativeRatingModal"
          >
            {{ t('cancel') }}
          </Button>
          <Button
            :disabled="!ratingNegativeVariant || isSubmittingRating"
            class="bg-purple-600 text-white hover:bg-purple-700"
            @click="submitNegativeRating"
          >
            <RotateCw
              v-if="isSubmittingRating"
              :size="14"
              class="mr-2 animate-spin"
            />
            {{ t('submitFeedback') }}
          </Button>
        </div>
      </div>
    </div>

    <!-- Workflow Negative Rating Modal -->
    <div
      v-if="workflowRatingModalOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      @click.self="closeNegativeWorkflowRatingModal"
    >
      <div class="mx-4 w-full max-w-md overflow-hidden rounded-lg bg-white shadow-xl">
        <!-- Modal Header -->
        <div class="flex items-center justify-between border-b border-gray-200 p-4">
          <h3 class="text-lg font-semibold text-gray-900">{{ t('negativeFeedback') }}</h3>
          <button
            class="text-gray-400 transition-colors hover:text-gray-600"
            @click="closeNegativeWorkflowRatingModal"
          >
            <X :size="20" />
          </button>
        </div>

        <!-- Modal Content -->
        <div class="space-y-4 p-4">
          <!-- Comment -->
          <div>
            <label class="mb-1 block text-sm font-medium text-gray-700">
              {{ t('additionalComments') }}
            </label>
            <textarea
              v-model="workflowRatingComment"
              rows="3"
              class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500
                focus:outline-none focus:ring-1 focus:ring-purple-500"
            ></textarea>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="flex justify-end gap-2 border-t border-gray-200 p-4">
          <Button
            variant="secondary"
            @click="closeNegativeWorkflowRatingModal"
          >
            {{ t('cancel') }}
          </Button>
          <Button
            :disabled="isSubmittingWorkflowRating"
            class="bg-purple-600 text-white hover:bg-purple-700"
            @click="submitNegativeWorkflowRating"
          >
            <RotateCw
              v-if="isSubmittingWorkflowRating"
              :size="14"
              class="mr-2 animate-spin"
            />
            {{ t('submitFeedback') }}
          </Button>
        </div>
      </div>
    </div>

    <!-- AI Actions Card -->
    <div class="">
      <div class="rounded-lg p-2">
        <!-- Header -->
        <!--        <div class="card-header">-->
        <!--          <div class="flex w-full items-center justify-between">-->
        <!--            <div class="flex items-center gap-3">-->
        <!--              <h4 class="mb-0 text-sm font-medium text-gray-900">{{ t('aiActions') }}</h4>-->
        <!--              <div-->
        <!--                v-if="totalActionsCount > 0"-->
        <!--                class="min-w-[18px] rounded-full bg-purple-100 px-1.5 py-0.5 text-center text-xs font-medium leading-none text-purple-700"-->
        <!--              >-->
        <!--                {{ totalActionsCount }}-->
        <!--              </div>-->
        <!--            </div>-->
        <!--          </div>-->
        <!--        </div>-->

        <!-- Content -->
        <div class="p-0">
          <!-- No actions message -->
          <div
            v-if="filteredActions.length === 0"
            class="px-4 py-6 text-center text-sm text-gray-400"
          >
            {{ t('noActionsInCategory') }}
          </div>

          <!-- Action Items -->
          <div
            v-else
            class="space-y-0"
          >
            <template
              v-for="(action, index) in displayedActions"
              :key="`action-${action.uuid}-${action.updated_at || action.created_at}`"
            >
              <div
                v-if="action && action.uuid"
                class="border-b border-gray-100 p-4"
                :class="{ 'border-b-0': index === displayedActions.length - 1 }"
              >
                <!-- Action Header -->
                <div class="mb-3 flex items-start justify-between">
                  <div class="flex items-center gap-2">
                    <component
                      :is="getActionInfo(action).icon"
                      :size="16"
                      class="text-gray-600"
                    />
                    <h5 class="text-sm font-medium text-gray-900">{{ getActionTitle(action) }}</h5>
                  </div>
                  <div class="flex items-center gap-2 text-xs text-gray-500">
                    {{ formatTimeAgo(action.created_at) }}
                    <!--
                    <span class="ml-2 flex items-center gap-1 text-gray-400">
                      <template v-if="action.is_committed">
                        <div
                          v-if="action.commited_by_uuid"
                          class="h-2 w-2 rounded-full bg-green-400"
                        ></div>
                        <div
                          v-else
                          class="h-2 w-2 rounded-full bg-purple-400"
                        ></div>
                        {{ action.commited_by_uuid ? t('executedByUser') : t('executedByAi') }}
                      </template>
                      <template v-else-if="action.is_reverted">
                        <div class="h-2 w-2 rounded-full bg-red-400"></div>
                        {{ action.reverted_by_uuid ? t('cancelledByUser') : t('cancelledByAi') }}
                      </template>
                      <template v-else>
                        <div class="h-2 w-2 rounded-full bg-yellow-400"></div>
                        {{ t('tabWaitingForConfirmation') }}
                      </template>
                    </span>
                    -->
                    <!-- Thumbs up button -->
                    <button
                      class="ml-2 flex h-6 w-6 items-center justify-center rounded border shadow-sm transition-colors"
                      :class="{
                        'border-purple-600 bg-purple-600 text-white':
                          actionRatings[action.uuid] === 'positive',
                        'border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700':
                          !actionRatings[action.uuid] && !isSubmittingRating,
                        'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400':
                          actionRatings[action.uuid] === 'negative'
                      }"
                      :disabled="!!actionRatings[action.uuid] || isSubmittingRating"
                      @click.stop="handlePositiveRating(action.uuid)"
                    >
                      <RotateCw
                        v-if="isSubmittingRating && !actionRatings[action.uuid]"
                        :size="14"
                        class="animate-spin"
                      />
                      <ThumbsUp
                        v-else
                        :size="14"
                      />
                    </button>
                    <!-- Thumbs down button -->
                    <button
                      class="flex h-6 w-6 items-center justify-center rounded border shadow-sm transition-colors"
                      :class="{
                        'border-purple-600 bg-purple-600 text-white':
                          actionRatings[action.uuid] === 'negative',
                        'border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700':
                          !actionRatings[action.uuid],
                        'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400':
                          actionRatings[action.uuid] === 'positive'
                      }"
                      :disabled="!!actionRatings[action.uuid] || isSubmittingRating"
                      @click.stop="openNegativeRatingModal(action.uuid)"
                    >
                      <ThumbsDown :size="14" />
                    </button>
                    <!-- Info button that opens up the AI reasoning popover -->
                    <div class="relative">
                      <button
                        class="flex h-6 w-6 items-center justify-center rounded border border-gray-200 bg-white text-gray-500
                          shadow-sm transition-colors hover:bg-gray-50 hover:text-gray-700"
                        @click.stop="toggleReasoningPopover(action.uuid)"
                      >
                        <Info :size="14" />
                      </button>
                      <!-- AI Reasoning Popover -->
                      <div
                        v-if="openReasoningPopover === action.uuid"
                        class="absolute right-0 top-7 z-50 w-96 rounded-lg border border-gray-200 bg-white p-4 shadow-xl"
                      >
                        <div class="mb-2 flex items-center justify-between">
                          <div class="rounded bg-purple-100 px-1.5 py-0.5 text-xs text-purple-600">
                            {{ t('ai') }}
                          </div>
                          <button
                            class="text-gray-400 transition-colors hover:text-gray-600"
                            @click.stop="openReasoningPopover = null"
                          >
                            <X :size="14" />
                          </button>
                        </div>
                        <p class="text-sm leading-relaxed text-gray-600">
                          {{
                            action.reasoning_translation?.[
                              locale as keyof typeof action.reasoning_translation
                            ] || t('processing')
                          }}
                        </p>
                      </div>
                    </div>
                    <!-- Debug button - only visible in debug mode -->
                    <a
                      v-if="isDebugMode && action.langfuse_trace_url"
                      :href="action.langfuse_trace_url"
                      target="_blank"
                      class="flex h-6 w-6 items-center justify-center rounded border border-orange-300 bg-orange-50
                        text-orange-600 shadow-sm transition-colors hover:bg-orange-100 hover:text-orange-700"
                    >
                      <ExternalLink :size="14" />
                    </a>
                  </div>
                </div>

                <!-- Widget Content and Actions Container -->
                <div class="rounded-lg border border-gray-200 p-4">
                  <!-- Render the actual widget component for content only -->
                  <component
                    :is="getWidgetComponent(action.action_type)"
                    v-if="getWidgetComponent(action.action_type)"
                    :action="action"
                    :custom-payload="getCustomPayload(action.uuid)"
                    :compact-mode="true"
                    @payload-changed="(payload: any) => handlePayloadChanged(action.uuid, payload)"
                  />
                  <div
                    v-else
                    class="text-sm text-gray-500"
                  >
                    {{ t('unknownAction') }}: {{ action.action_type }}
                  </div>

                  <!-- Action Buttons - Only show for pending actions -->
                  <div
                    v-if="!action.is_committed && !action.is_reverted"
                    class="mt-4 flex items-center justify-end gap-2"
                  >
                    <button
                      class="rounded-md border border-gray-300 bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700
                        transition-colors hover:border-gray-400 hover:bg-gray-200 disabled:cursor-not-allowed
                        disabled:opacity-50"
                      :disabled="
                        action.is_committing ||
                        action.is_reverting ||
                        committingActions.has(action.uuid) ||
                        revertingActions.has(action.uuid)
                      "
                      @click="handleActionReverted(action.uuid)"
                    >
                      <div class="flex items-center gap-2">
                        <RotateCw
                          v-if="revertingActions.has(action.uuid)"
                          :size="14"
                          class="animate-spin"
                        />
                        {{ t('cancel') }}
                      </div>
                    </button>
                    <button
                      class="rounded-md border border-purple-600 bg-purple-600 px-4 py-2 text-sm font-medium text-white
                        transition-colors hover:border-purple-700 hover:bg-purple-700 disabled:cursor-not-allowed
                        disabled:opacity-50"
                      :disabled="
                        action.is_committing ||
                        action.is_reverting ||
                        committingActions.has(action.uuid) ||
                        revertingActions.has(action.uuid)
                      "
                      @click="handleActionCommitted(action.uuid)"
                    >
                      <div class="flex items-center gap-2">
                        <RotateCw
                          v-if="committingActions.has(action.uuid)"
                          :size="14"
                          class="animate-spin"
                        />
                        {{ t('confirm') }}
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- Bottom Bar: View All AI Reasoning + Feedback Buttons -->
    <div class="flex items-center justify-between border-t border-gray-200 px-4 py-3">
      <!-- View All AI Reasoning Button (left) -->
      <button
        v-if="allReasonings.length > 1"
        class="text-xs text-purple-600 hover:text-purple-700 hover:underline"
        @click="showAllReasoningsModal = true"
      >
        {{ viewAllText }}
      </button>
      <div v-else></div>

      <!-- Workflow Rating Buttons (right) -->
      <div class="flex items-center gap-1.5">
        <button
          class="flex h-7 w-7 items-center justify-center rounded border shadow-sm transition-colors"
          :class="{
            'border-purple-600 bg-purple-600 text-white': workflowRating === 'positive',
            'border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700':
              !workflowRating && !isSubmittingWorkflowRating,
            'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400':
              workflowRating === 'negative'
          }"
          :disabled="!!workflowRating || isSubmittingWorkflowRating"
          @click="handlePositiveWorkflowRating"
        >
          <RotateCw
            v-if="isSubmittingWorkflowRating"
            :size="12"
            class="animate-spin"
          />
          <ThumbsUp
            v-else
            :size="12"
          />
        </button>
        <button
          class="flex h-7 w-7 items-center justify-center rounded border shadow-sm transition-colors"
          :class="{
            'border-purple-600 bg-purple-600 text-white': workflowRating === 'negative',
            'border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700':
              !workflowRating,
            'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400':
              workflowRating === 'positive'
          }"
          :disabled="!!workflowRating || isSubmittingWorkflowRating"
          @click="openNegativeWorkflowRatingModal"
        >
          <ThumbsDown :size="12" />
        </button>
        <!-- Debug button - only visible in debug mode -->
        <a
          v-if="isDebugMode && caseData?.case_lifecycle_state?.langfuse_session_url"
          :href="caseData.case_lifecycle_state.langfuse_session_url"
          target="_blank"
          class="flex h-7 w-7 items-center justify-center rounded border border-orange-300 bg-orange-50
            text-orange-600 shadow-sm transition-colors hover:bg-orange-100 hover:text-orange-700"
        >
          <ExternalLink :size="12" />
        </a>
      </div>
    </div>
  </div>
</template>
